import React from 'react';

import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  FormControl as MuiFormControl,
  TextField,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import type { Control } from 'react-hook-form';

import { logger } from '@/utils/logger';

import { useCustomReactFlowState } from '../../context/reactflow-context';
import ExpandableField from './expandable-field';
import { JSONEditor } from './json-editor';
import { SparqlEditor } from './sparql-editor';

export const FORM_FIELD_TYPES = {
  TEXT: 'text',
  CHECKBOX: 'checkbox',
  SELECT: 'select',
  TEXTAREA: 'textarea',
  SPARKQL: 'sparkql',
  JSON: 'json',
  DATE: 'date',
} as const;

interface FormFieldProps {
  controlId?: string;
  label?: string;
  name: string;
  control: Control<Record<string, unknown>>;
  type?: string;
  fieldComponentType: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  error?: string;
  defaultValue?: unknown;
}

export const FormField: React.FC<FormFieldProps> = ({
  controlId,
  label,
  name,
  control,
  type = 'text',
  fieldComponentType,
  placeholder,
  required,
  disabled,
  children,
  error,
  defaultValue,
}) => {
  const { isReadOnly } = useCustomReactFlowState();

  const canExpand = [
    FORM_FIELD_TYPES.TEXT,
    FORM_FIELD_TYPES.TEXTAREA,
    FORM_FIELD_TYPES.SPARKQL,
    FORM_FIELD_TYPES.JSON,
  ].includes(fieldComponentType);

  const renderField = (
    componentClass: string,
    field: { value: unknown; onChange: (v: unknown) => void; name: string }
  ) => {
    switch (componentClass) {
      case FORM_FIELD_TYPES.SELECT:
        return (
          <TextField
            {...field}
            value={field.value ?? defaultValue ?? ''}
            id={controlId || `${name}-${Math.random().toString(36).substring(2, 9)}`}
            name={name}
            label={label}
            disabled={disabled || isReadOnly}
            fullWidth
            size="small"
            error={!!error}
            select
            required={required}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
          >
            {children}
          </TextField>
        );
      case FORM_FIELD_TYPES.TEXTAREA:
        return canExpand ? (
          <ExpandableField
            fieldType="textarea"
            label={label || ''}
            value={field.value}
            onChange={field.onChange}
            disabled={disabled || isReadOnly}
            placeholder={placeholder}
          >
            <TextField
              multiline
              rows={10}
              {...field}
              id={controlId || `${name}-${Math.random().toString(36).substring(2, 9)}`}
              name={name}
              label={label}
              placeholder={placeholder}
              defaultValue={defaultValue}
              disabled={disabled || isReadOnly}
              fullWidth
              size="small"
              error={!!error}
              required={required}
            />
          </ExpandableField>
        ) : (
          <TextField
            multiline
            rows={10}
            {...field}
            id={controlId || `${name}-${Math.random().toString(36).substring(2, 9)}`}
            name={name}
            label={label}
            placeholder={placeholder}
            defaultValue={defaultValue}
            disabled={disabled || isReadOnly}
            fullWidth
            size="small"
            error={!!error}
            required={required}
          />
        );
      case FORM_FIELD_TYPES.CHECKBOX:
        return (
          <FormControlLabel
            control={
              <Checkbox
                {...field}
                defaultChecked={defaultValue}
                checked={field?.value || defaultValue || false}
                disabled={disabled || isReadOnly}
              />
            }
            label={label || ''}
          />
        );
      case FORM_FIELD_TYPES.TEXT:
        return canExpand ? (
          <ExpandableField
            fieldType="text"
            label={label || ''}
            value={field.value}
            onChange={field.onChange}
            disabled={disabled || isReadOnly}
            placeholder={placeholder}
          >
            <TextField
              type={type}
              {...field}
              id={controlId || `${name}-${Math.random().toString(36).substring(2, 9)}`}
              name={name}
              label={label}
              placeholder={placeholder}
              disabled={disabled || isReadOnly}
              defaultValue={defaultValue}
              fullWidth
              size="small"
              error={!!error}
              required={required}
            />
          </ExpandableField>
        ) : (
          <TextField
            type={type}
            {...field}
            id={controlId || `${name}-${Math.random().toString(36).substring(2, 9)}`}
            name={name}
            label={label}
            placeholder={placeholder}
            disabled={disabled || isReadOnly}
            defaultValue={defaultValue}
            fullWidth
            size="small"
            error={!!error}
            required={required}
          />
        );
      case FORM_FIELD_TYPES.SPARKQL:
        return (
          <ExpandableField
            fieldType="sparkql"
            label={label || ''}
            value={field.value}
            onChange={field.onChange}
            disabled={disabled || isReadOnly}
            fieldProps={{
              renderExpandedContent: (value: unknown, setValue: (v: unknown) => void) => (
                <div
                  style={{
                    height: '100%',
                    width: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                  }}
                >
                  <SparqlEditor
                    value={value}
                    onChange={setValue}
                    disabled={disabled || isReadOnly}
                    height="100%"
                  />
                </div>
              ),
            }}
          >
            <SparqlEditor
              value={field.value}
              onChange={field.onChange}
              defaultValue={defaultValue}
              disabled={disabled || isReadOnly}
            />
          </ExpandableField>
        );
      case FORM_FIELD_TYPES.JSON:
        return (
          <ExpandableField
            fieldType="json"
            label={label || ''}
            value={field.value}
            onChange={field.onChange}
            disabled={disabled || isReadOnly}
            fieldProps={{
              renderExpandedContent: (value: unknown, setValue: (v: unknown) => void) => (
                <div
                  style={{
                    height: '100%',
                    width: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                  }}
                >
                  <JSONEditor
                    value={value}
                    onChange={setValue}
                    disabled={disabled || isReadOnly}
                    height="100%"
                  />
                </div>
              ),
            }}
          >
            <JSONEditor
              value={field.value}
              onChange={field.onChange}
              defaultValue={defaultValue}
              disabled={disabled || isReadOnly}
            />
          </ExpandableField>
        );
      case FORM_FIELD_TYPES.DATE:
        return (
          <TextField
            type="date"
            {...field}
            label={label}
            placeholder={placeholder}
            disabled={disabled || isReadOnly}
            defaultValue={defaultValue}
            fullWidth
            size="small"
            error={!!error}
            required={required}
            InputLabelProps={{ shrink: true }}
          />
        );

      default:
        logger.error(`Unsupported component class: ${componentClass}`);
        return null;
    }
  };

  return (
    <MuiFormControl fullWidth error={!!error} sx={{ mb: 2 }}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => renderField(fieldComponentType, field)}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </MuiFormControl>
  );
};
